import React from 'react';
import { Facebook, X, Share2, Bookmark, MessageSquare } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/use-toast';

interface SocialShareButtonsProps {
  url: string;
  title: string;
  description?: string;
  image?: string;
  commentCount?: number;
}

const SocialShareButtons: React.FC<SocialShareButtonsProps> = ({
  url,
  title,
  description = '',
  image = '',
  commentCount = 0
}) => {
  const { resolvedTheme } = useTheme();
  const iconColor = resolvedTheme === 'dark' ? '#fff' : '#222222';

  const getShareUrl = () => {
    // Use the actual story URL for sharing
    return url;
  };

  const handleShare = (platform: string) => {
    const shareUrl = getShareUrl();
    let platformShareUrl = '';
    
    switch (platform) {
      case 'instagram':
        // Instagram doesn't have a direct URL share, so use native share or copy to clipboard
        if (navigator.share) {
          navigator.share({
            title,
            text: description,
            url
          }).catch(err => {
            console.error('Error sharing:', err);
            copyToClipboard();
          });
          return;
        }
        copyToClipboard();
        return;
      case 'facebook':
        platformShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'twitter':
        platformShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'pinterest':
        platformShareUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}${image ? `&media=${encodeURIComponent(image)}` : ''}&description=${encodeURIComponent(title)}`;
        break;
      default:
        if (navigator.share) {
          navigator.share({
            title,
            text: description,
            url
          }).catch(err => {
            console.error('Error sharing:', err);
            copyToClipboard();
          });
          return;
        }
        copyToClipboard();
        return;
    }
    
    window.open(platformShareUrl, '_blank', 'width=600,height=400');
  };

  const copyToClipboard = () => {
    const shareUrl = getShareUrl();
    navigator.clipboard.writeText(shareUrl).then(() => {
      toast({
        title: "Link copied",
        description: "Story link has been copied to clipboard",
      });
    }).catch(() => {
      toast({
        title: "Unable to copy link",
        description: "Please try again or copy the URL manually",
        variant: "destructive"
      });
    });
  };

  const scrollToComments = () => {
    const commentsSection = document.getElementById('comments');
    if (commentsSection) {
      commentsSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };

  const buttonClass = "p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors";

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => handleShare('instagram')}
        className={buttonClass}
        aria-label="Share on Instagram"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill={iconColor}>
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      </button>
      <button
        onClick={() => handleShare('facebook')}
        className={buttonClass}
        aria-label="Share on Facebook"
      >
        <Facebook size={20} color={iconColor} />
      </button>
      <button
        onClick={() => handleShare('twitter')}
        className={buttonClass}
        aria-label="Share on Twitter"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill={iconColor}>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      </button>
      <button
        onClick={() => handleShare('pinterest')}
        className={buttonClass}
        aria-label="Share on Pinterest"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill={iconColor}>
          <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.024-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.746-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001 12.017.001z"/>
        </svg>
      </button>
      <button
        onClick={() => handleShare('general')}
        className={buttonClass}
        aria-label="Share"
      >
        <Share2 size={20} color={iconColor} />
      </button>
      <button
        onClick={scrollToComments}
        className={buttonClass}
        aria-label="View Comments"
      >
        <MessageSquare size={20} color={iconColor} />
        {commentCount > 0 && (
          <span className="ml-1 text-xs absolute -top-1 -right-1 bg-primary text-white rounded-full px-1.5 py-0.5">
            {commentCount}
          </span>
        )}
      </button>
    </div>
  );
};

export default SocialShareButtons;
